import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '../form/Button';

const BUTTON_STYLE = { color: 'white' };
const FIELD_STYLE = { width: '100%', boxSizing: 'border-box', marginTop: 4, padding: 6 };
const LABEL_STYLE = { display: 'block', marginTop: 12, fontSize: 13, fontWeight: 700 };
const HINT_STYLE = { fontSize: 12, color: '#666', marginTop: 4 };

/**
 * Modal for creating / editing a hyperlink in the Tiptap editor. Controlled by
 * the toolbar: it owns the open flag and seeds the initial values, this
 * component owns the in-flight url/text.
 *
 * Click and mousedown are stopped at the root — the dialog renders through a
 * portal, but React still bubbles its events up the component tree, where the
 * toolbar preventDefaults mousedown (to keep the editor selection) and the
 * editor wrapper refocuses the editor on click. Both would break typing here.
 *
 * Props:
 *  - open: boolean
 *  - initialUrl / initialText: seed values, re-read on every open
 *  - canRemove: boolean — show the "remove link" action (editing an existing link)
 *  - onSubmit({url, text}): confirm
 *  - onRemove(): drop the link mark
 *  - onClose(): dismiss without changes
 */
class LinkDialog extends Component {
  state = { url: '', text: '' };

  componentDidUpdate(prevProps) {
    if (this.props.open && !prevProps.open) {
      this.setState({
        url: this.props.initialUrl || '',
        text: this.props.initialText || '',
      });
    }
  }

  submit = () => {
    const url = (this.state.url || '').trim();
    if (!url) return;
    this.props.onSubmit({ url, text: (this.state.text || '').trim() });
  };

  handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      this.submit();
    }
  };

  stopEvent = (e) => {
    e.stopPropagation();
  };

  render() {
    const { open, canRemove, onRemove, onClose } = this.props;
    const { url, text } = this.state;

    return (
      <Dialog
        open={!!open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        onClick={this.stopEvent}
        onMouseDown={this.stopEvent}
      >
        <DialogTitle>{canRemove ? 'Upravit odkaz' : 'Vložit odkaz'}</DialogTitle>
        <DialogContent>
          <label style={LABEL_STYLE}>
            Adresa odkazu (URL)
            <input
              type="text"
              value={url}
              autoFocus
              onChange={(e) => this.setState({ url: e.target.value })}
              onKeyDown={this.handleKeyDown}
              placeholder="např. www.mvcr.cz nebo podatelna@mvcr.cz"
              style={FIELD_STYLE}
            />
          </label>
          <div style={HINT_STYLE}>
            Chybí-li „https://“, doplní se automaticky. E-mailová adresa se změní
            na odkaz „napsat e-mail“.
          </div>

          <label style={LABEL_STYLE}>
            Text odkazu
            <input
              type="text"
              value={text}
              onChange={(e) => this.setState({ text: e.target.value })}
              onKeyDown={this.handleKeyDown}
              placeholder="Nevyplněno = použije se adresa odkazu"
              style={FIELD_STYLE}
            />
          </label>
          <div style={HINT_STYLE}>
            Necháte-li text beze změny, zachová se původní formátování označeného
            textu. Přepsáním textu se formátování odstraní.
          </div>
        </DialogContent>
        <DialogActions>
          <Button
            type="button"
            style={{ ...BUTTON_STYLE, marginRight: 8 }}
            label={canRemove ? 'Uložit' : 'Vložit'}
            disabled={!url.trim()}
            onClick={this.submit}
          />
          {canRemove && (
            <Button
              type="button"
              style={{ ...BUTTON_STYLE, marginRight: 8 }}
              label="Odstranit odkaz"
              onClick={onRemove}
            />
          )}
          <Button type="button" style={BUTTON_STYLE} label="Zrušit" onClick={onClose} />
        </DialogActions>
      </Dialog>
    );
  }
}

LinkDialog.propTypes = {
  open: PropTypes.bool,
  initialUrl: PropTypes.string,
  initialText: PropTypes.string,
  canRemove: PropTypes.bool,
  onSubmit: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default LinkDialog;
